import React, { useState } from "react";
import {
  Modal,
  Button,
  Group,
  Stack,
  Title,
  Alert,
  Loader,
  PinInput,
  Text,
  Divider,
  Select,
} from "@mantine/core";
import {
  IconAlertCircle,
  IconShieldCheck,
  IconLock,
} from "@tabler/icons-react";
import { useAuth } from "../../context/AuthContext";
import { getApiEndpoint } from "../../config/config";
import {
  DEFAULT_PIN_UNLOCK_DURATION,
  PIN_UNLOCK_DURATIONS,
  startPinUnlockSession,
} from "../../utils/pinUnlockSession";

function PinVerificationModal({
  isOpen,
  onClose,
  onSuccess,
  title = "Verify PIN",
  showDurationSelector = true,
}) {
  const { getVerifiedJWT } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pin, setPin] = useState("");
  const [unlockDuration, setUnlockDuration] = useState(
    String(DEFAULT_PIN_UNLOCK_DURATION)
  );

  const handleVerifyPin = async () => {
    try {
      setLoading(true);
      setError(null);

      // Validate PIN
      if (!pin || pin.length !== 4) {
        setError("Please enter a 4-digit PIN");
        return;
      }

      const jwt = await getVerifiedJWT();
      if (!jwt) {
        setError("Authentication failed");
        return;
      }

      const response = await fetch(getApiEndpoint("/v1/user/verify-pin"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwt}`,
        },
        body: JSON.stringify({
          pin: pin,
        }),
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success || data.data?.verified) {
          startPinUnlockSession({
            pin,
            durationMinutes: Number(unlockDuration),
          });
          // Success - call the success callback with the PIN
          onSuccess(pin, Number(unlockDuration));
          handleCancel(); // Close and reset
        } else {
          setError("Invalid PIN. Please try again.");
          setPin(""); // Clear PIN on failure
        }
      } else {
        const data = await response.json();
        setError(data.message || "Invalid PIN. Please try again.");
        setPin(""); // Clear PIN on failure
      }
    } catch (err) {
      setError("Failed to verify PIN. Please try again.");
      setPin(""); // Clear PIN on failure
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    // Reset form data
    setPin("");
    setError(null);
    setUnlockDuration(String(DEFAULT_PIN_UNLOCK_DURATION));
    onClose();
  };

  const handlePinComplete = value => {
    setPin(value);
    // Auto-submit when PIN is complete
    if (value.length === 4) {
      // Small delay to show the complete PIN before verifying
      setTimeout(() => {
        handleVerifyPin();
      }, 300);
    }
  };

  return (
    <Modal
      opened={isOpen}
      onClose={handleCancel}
      title={
        <Group gap="xs">
          <IconLock size={20} />
          <Title order={3} fw={600}>
            {title}
          </Title>
        </Group>
      }
      size="sm"
      padding="xl"
      closeOnClickOutside={!loading}
      closeOnEscape={!loading}
      centered
    >
      <Stack gap="lg">
        {error && (
          <Alert
            icon={<IconAlertCircle size={16} />}
            title="Error"
            color="red"
            variant="light"
          >
            {error}
          </Alert>
        )}

        <Text c="dimmed" size="sm" ta="center">
          Please enter your 4-digit security PIN to access pay scale details.
        </Text>

        <Divider />

        {/* PIN Input Section */}
        <Stack gap="md" align="center">
          <Text fw={600} size="sm">
            Enter Your PIN
          </Text>
          <PinInput
            length={4}
            value={pin}
            onChange={setPin}
            onComplete={handlePinComplete}
            placeholder="○"
            type="number"
            size="xl"
            disabled={loading}
            mask
            styles={{
              input: {
                borderColor: error ? "#fa5252" : undefined,
              },
            }}
          />
          {loading && (
            <Group gap="xs">
              <Loader size="sm" />
              <Text size="sm" c="dimmed">
                Verifying...
              </Text>
            </Group>
          )}
        </Stack>

        {showDurationSelector && (
          <Select
            label="Keep unlocked for"
            value={unlockDuration}
            onChange={value =>
              setUnlockDuration(value || String(DEFAULT_PIN_UNLOCK_DURATION))
            }
            data={PIN_UNLOCK_DURATIONS.map(option => ({
              value: String(option.value),
              label: option.label,
            }))}
            allowDeselect={false}
          />
        )}

        {/* Action Buttons */}
        <Group justify="space-between" gap="sm" pt="md">
          <Button
            variant="outline"
            onClick={handleCancel}
            disabled={loading}
            size="sm"
          >
            Cancel
          </Button>
          <Button
            onClick={handleVerifyPin}
            loading={loading}
            leftSection={loading ? undefined : <IconShieldCheck size={16} />}
            disabled={pin.length !== 4}
            size="sm"
          >
            Verify PIN
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

export default PinVerificationModal;
